import { DocMap, FieldDefinition, FieldsMap } from ".";
import { shortDateString } from "../../utils/dateFormatters";
import { enumToOptions } from "../../utils/enumToOptions";
import { formatCurrency } from "../../utils/numberFormatters";
import {
  Show,
  ShowDealType,
  ExpenseTag,
} from "@quantizr-engineering/common/types/show";
import microphone from "../../assets/microphone01.svg";

const agreementExpenseField: FieldDefinition = {
  name: "expenses",
  label: "Expenses",
  type: "array",
  required: true,
  subfields: [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
    },
    {
      name: "tag",
      label: "Tag",
      type: "select",
      required: true,
    },
    {
      name: "cost",
      label: "Budget",
      type: "currency",
      required: true,
      calcTotal: (show: Show) => {
        const total = show.expenses.reduce(
          (total, expense) => total + expense.cost,
          0
        );
        return formatCurrency(total);
      },
    },
  ],
};

const settlementExpenseField: FieldDefinition = {
  name: "expenses",
  label: "Expenses",
  type: "array",
  required: true,
  subfields: [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
    },
    {
      name: "tag",
      label: "Tag",
      type: "select",
      required: true,
    },
    {
      name: "actualCost",
      label: "Cost",
      type: "currency",
      required: true,
      calcTotal: (show: Show) => {
        const total = show.expenses.reduce(
          (total, expense) => total + (expense.actualCost ?? 0),
          0
        );
        return formatCurrency(total);
      },
    },
  ],
};

const agreementTicketTypesField: FieldDefinition = {
  name: "ticketTypes",
  label: "Ticket Types",
  type: "array",
  required: true,
  subfields: [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
    },
    {
      name: "price",
      label: "Price",
      type: "currency",
      required: true,
      calcTotal: (show: Show) => {
        const total = show.ticketTypes.reduce(
          (total, tType) => total + (tType.price ?? 0) * (tType.quantity ?? 0),
          0
        );
        return formatCurrency(total);
      },
      totalTooltipText: "Gross Ticket Potential",
    },
    {
      name: "quantity",
      label: "Quantity",
      type: "number",
      required: true,
      calcTotal: (show: Show) => {
        return show.ticketTypes.reduce(
          (total, tType) => total + (tType.quantity ?? 0),
          0
        );
      },
    },
    {
      name: "charityFee",
      label: "Charity Fee",
      type: "number",
      required: true,
    },
    {
      name: "facilityFee",
      label: "Facility Fee",
      type: "number",
      required: true,
    },
  ],
};

const settlementTicketTypesField: FieldDefinition = {
  name: "ticketTypes",
  label: "Ticket Types",
  type: "array",
  required: true,
  subfields: [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
    },
    {
      name: "price",
      label: "Price",
      type: "currency",
      required: true,
      calcTotal: (show: Show) => {
        const total = show.ticketTypes.reduce(
          (total, tType) => total + (tType.price ?? 0) * (tType.sold ?? 0),
          0
        );
        return formatCurrency(total);
      },
      totalTooltipText: "Gross Box Office",
    },
    {
      name: "sold",
      label: "Sold",
      type: "number",
      required: true,
      calcTotal: (show: Show) => {
        return show.ticketTypes.reduce(
          (total, tType) => total + (tType.sold ?? 0),
          0
        );
      },
    },
    {
      name: "charityFee",
      label: "Charity Fee",
      type: "number",
      required: true,
    },
    {
      name: "facilityFee",
      label: "Facility Fee",
      type: "number",
      required: true,
    },
  ],
};

const liveSettlementFieldsMap: FieldsMap = {
  expenses: settlementExpenseField,
  ticketTypes: settlementTicketTypesField,
};

const liveAgreementFieldsMap: FieldsMap = {
  venue: {
    name: "venue",
    label: "Venue",
    type: "text",
    required: true,
  },
  city: {
    name: "city",
    label: "City",
    type: "text",
    required: true,
  },
  state: {
    name: "state",
    label: "State",
    type: "text",
    required: true,
  },
  description: {
    name: "description",
    label: "Description",
    type: "text",
    required: true,
  },
  appearingWith: {
    name: "appearingWith",
    label: "Appearing With",
    type: "text",
    required: true,
  },
  additionalPoints: {
    name: "additionalPoints",
    label: "Additional Points",
    type: "text",
    required: true,
  },
  dealType: {
    name: "dealType",
    label: "Deal Type",
    type: "select",
    options: enumToOptions(ShowDealType),
    required: true,
  },
  profitSharePercent: {
    name: "profitSharePercent",
    label: "Profit Share %",
    type: "percent",
    required: true,
  },
  merchSoft: {
    name: "merchSoft",
    label: "Merch Soft",
    type: "percent",
    required: true,
  },
  merchHard: {
    name: "merchHard",
    label: "Merch Hard",
    type: "percent",
    required: true,
  },
  performanceDate: {
    name: "performanceDate",
    label: "Performance Date",
    type: "date",
    required: true,
  },
  guarantee: {
    name: "guarantee",
    label: "Guarantee",
    type: "currency",
    required: true,
  },
  tax: {
    name: "tax",
    label: "Tax",
    type: "currency",
    required: true,
  },
  ticketTypes: agreementTicketTypesField,
  expenses: agreementExpenseField,
  bonuses: {
    name: "bonuses",
    label: "Bonuses",
    type: "array",
    required: true,
    subfields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
      },
      {
        name: "threshold",
        label: "Threshold (amt)",
        type: "number",
        required: true,
      },
      {
        name: "thresholdPercent",
        label: "Threshold (%)",
        type: "number",
        required: true,
      },
      {
        name: "bonusAmount",
        label: "Bonus ($)",
        type: "currency",
        required: true,
        calcTotal: (show: Show) => {
          const total = show.ticketBonuses.reduce(
            (total, tBonus) => total + tBonus.bonusAmount,
            0
          );
          return formatCurrency(total);
        },
      },
      {
        name: "bonusPercent",
        label: "Bonus (%)",
        type: "number",
        required: true,
      },
    ],
  },
  buyouts: {
    name: "productionBuyouts",
    label: "Buyouts",
    type: "array",
    required: true,
    subfields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
      },
      {
        name: "amount",
        label: "Amount",
        type: "currency",
        required: true,
        calcTotal: (show: Show) => {
          const total = show.productionBuyouts.reduce(
            (total, buyout) => total + buyout.amount,
            0
          );
          return formatCurrency(total);
        },
      },
    ],
  },
  adjustments: {
    name: "adjustments",
    label: "Adjustments",
    type: "array",
    required: true,
    subfields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
      },
      {
        name: "notes",
        label: "Notes",
        type: "text",
        required: false,
      },
      {
        name: "amount",
        label: "Amount",
        type: "currency",
        required: true,
        calcTotal: (show: Show) => {
          if (!show.ticketAdjustments) return 0;
          const total = show.ticketAdjustments.reduce(
            (total, adjustment) => total + adjustment.amount,
            0
          );
          return formatCurrency(total);
        },
      },
      {
        name: "addToGross",
        label: "Apply to Gross",
        type: "boolean",
        required: true,
      },
    ],
  },
  deductions: {
    name: "deductions",
    label: "Deductions",
    type: "array",
    required: true,
    subfields: [
      {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
      },
      {
        name: "amount",
        label: "Amount",
        type: "currency",
        required: true,
        calcTotal: (show: Show) => {
          if (!show.deductions) return 0;
          const total = show.deductions.reduce(
            (total, deduction) => total + deduction.amount,
            0
          );
          return formatCurrency(total);
        },
      },
    ],
  },
  deposits: {
    name: "deposits",
    label: "Deposits",
    type: "array",
    required: true,
    subfields: [
      {
        name: "amount",
        label: "Amount",
        type: "currency",
        required: true,
        calcTotal: (show: Show) => {
          const total = show.deposits.reduce(
            (total, deposit) => total + deposit.amount,
            0
          );
          return formatCurrency(total);
        },
      },
    ],
  },
};

const liveAgreementSummaryMap: FieldsMap = {
  partiesAndDate: {
    name: "partiesAndDate",
    label: "Parties and Date",
    type: "text",
    required: true,
    subfields: [
      {
        name: "performingArtist",
        label: "Artist",
        type: "text",
        required: true,
      },
      {
        name: "venue",
        label: "Venue",
        type: "text",
        required: true,
      },
      {
        name: "promoter",
        label: "Promoter",
        type: "text",
        required: true,
      },
      {
        name: "performanceDate",
        label: "Date",
        type: "date",
        required: true,
      },
    ],
  },
  keyPoints: {
    name: "keyPoints",
    label: "Key Points",
    type: "text",
    required: true,
    subfields: [
      {
        name: "dealType",
        label: "Deal Type",
        type: "select",
        options: enumToOptions(ShowDealType),
        required: true,
      },
      {
        name: "guarantee",
        label: "Guarantee",
        type: "currency",
        required: true,
      },
      {
        name: "profitSharePercent",
        label: "Revenue Share %",
        type: "percent",
        required: true,
      },
    ],
  },
};

const creedSummaryFieldsMap: FieldsMap = {
  // TODO: Implement this summary field map
};

const creedDataFieldsMap: FieldsMap = {
  performanceDate: {
    name: "performanceDate",
    label: "Performance Date",
    required: false,
    type: "date",
  },

  venue: {
    name: "venue",
    label: "Venue",
    required: false,
    type: "text",
  },

  promoter: {
    name: "promoter",
    label: "Promoter",
    required: false,
    type: "text",
  },

  appearingWith: {
    name: "appearingWith",
    label: "Appearing With",
    required: false,
    type: "text",
  },

  country: {
    name: "country",
    label: "Country",
    required: false,
    type: "text",
  },

  state: {
    name: "state",
    label: "State",
    required: false,
    type: "text",
  },

  city: {
    name: "city",
    label: "City",
    required: false,
    type: "text",
  },

  description: {
    name: "description",
    label: "Description",
    required: false,
    type: "text",
  },

  dealType: {
    name: "dealType",
    label: "Deal Type",
    required: false,
    type: "select",
    options: enumToOptions(ShowDealType),
  },

  guarantee: {
    name: "guarantee",
    label: "Guarantee",
    required: false,
    type: "number",
  },

  profitSharePercent: {
    name: "profitSharePercent",
    label: "Profit Share Percent",
    required: false,
    type: "number",
  },

  tax: {
    name: "tax",
    label: "Tax",
    required: false,
    type: "number",
  },

  merchSoft: {
    name: "merchSoft",
    label: "Merch Soft",
    required: false,
    type: "number",
  },

  merchHard: {
    name: "merchHard",
    label: "Merch Hard",
    required: false,
    type: "number",
  },

  sellableCap: {
    name: "sellableCap",
    label: "Sellable Cap",
    required: false,
    type: "number",
  },

  deposits: {
    name: "deposits",
    label: "Deposits",
    required: false,
    type: "array",
    subfields: [
      {
        name: "amount",
        label: "Amount",
        required: false,
        type: "number",
      },
      {
        name: "dueDate",
        label: "Due Date",
        required: false,
        type: "date",
      },
    ],
  },

  productionBuyouts: {
    name: "productionBuyouts",
    label: "Production Buyouts",
    required: false,
    type: "array",
    subfields: [
      {
        name: "name",
        label: "Name",
        required: false,
        type: "text",
      },
      {
        name: "amount",
        label: "Amount",
        required: false,
        type: "number",
      },
    ],
  },

  withholdings: {
    name: "withholdings",
    label: "Withholdings",
    required: false,
    type: "array",
    subfields: [
      {
        name: "name",
        label: "Name",
        required: false,
        type: "text",
      },
      {
        name: "amount",
        label: "Amount",
        required: false,
        type: "number",
      },
    ],
  },

  ticketTypes: {
    name: "ticketTypes",
    label: "Ticket Types",
    required: false,
    type: "array",
    subfields: [
      {
        name: "name",
        label: "Name",
        required: false,
        type: "text",
      },
      {
        name: "price",
        label: "Price",
        required: false,
        type: "number",
      },
      {
        name: "charityFee",
        label: "Charity Fee",
        required: false,
        type: "number",
      },
      {
        name: "facilityFee",
        label: "Facility Fee",
        required: false,
        type: "number",
      },
      {
        name: "quantity",
        label: "Quantity",
        required: false,
        type: "number",
      },
    ],
  },

  ticketBonuses: {
    name: "ticketBonuses",
    label: "Ticket Bonuses",
    required: false,
    type: "array",
    subfields: [
      {
        name: "bonusPercent",
        label: "Bonus Percent",
        required: false,
        type: "number",
      },
      {
        name: "bonusAmount",
        label: "Bonus Amount",
        required: false,
        type: "number",
      },
      {
        name: "threshold",
        label: "Threshold",
        required: false,
        type: "number",
      },
      {
        name: "thresholdPercent",
        label: "Threshold Percent",
        required: false,
        type: "number",
      },
    ],
  },

  expenses: {
    name: "expenses",
    label: "Expenses",
    required: false,
    type: "array",
    subfields: [
      {
        name: "cost",
        label: "Cost",
        required: false,
        type: "number",
      },
      {
        name: "name",
        label: "Name",
        required: false,
        type: "text",
      },
      {
        name: "expenseTag",
        label: "Expense Tag",
        required: false,
        type: "select",
        options: enumToOptions(ExpenseTag),
      },
      {
        name: "quantity",
        label: "Quantity",
        required: false,
        type: "number",
      },
      {
        name: "notes",
        label: "Notes",
        required: false,
        type: "text",
      },
    ],
  },

  additional_points: {
    name: "additional_points",
    label: "Additional Points",
    required: false,
    type: "array",
    subfields: [
      {
        name: "point",
        label: "Point",
        type: "text",
        required: false,
      },
    ],
  },
};

export const CreedShowOfferMap: DocMap = {
  summaryFields: creedSummaryFieldsMap,
  dataFields: creedDataFieldsMap,
  entityHref: (show: Show) => `/runs/${show.runID}/shows/${show.id}`,
  entityHrefIcon: () => microphone,
  entityHrefTitle: (show: Show) => show.venue,
  entityHrefSubtitle: (show: Show) =>
    `${shortDateString(show.performanceDate)}`,
};

export const liveAgreementDocMap: DocMap = {
  dataFields: liveAgreementFieldsMap,
  summaryFields: liveAgreementSummaryMap,
  entityHref: (show: Show) => `/runs/${show.runID}/shows/${show.id}`,
  entityHrefIcon: () => microphone,
  entityHrefTitle: (show: Show) => show.venue,
  entityHrefSubtitle: (show: Show) =>
    `${shortDateString(show.performanceDate)}`,
};

export const liveSettlementDocMap: DocMap = {
  dataFields: liveSettlementFieldsMap,
  summaryFields: liveAgreementSummaryMap,
  entityHref: (show: Show) => `/runs/${show.runID}/shows/${show.id}`,
  entityHrefIcon: () => microphone,
  entityHrefTitle: (show: Show) => show.venue,
  entityHrefSubtitle: (show: Show) =>
    `${shortDateString(show.performanceDate)} `,
};
